import React from 'react';
import { type PullRequest } from '@/shared/services/supabaseService';
import { GitPullRequest, GitMerge, GitPullRequestClosed } from 'lucide-react';
import { Badge } from '@/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/ui/avatar';
import { Card, CardHeader, CardTitle, CardDescription } from '@/ui/Card';

interface PullRequestHeaderProps {
  pr: PullRequest;
}

const PullRequestHeader: React.FC<PullRequestHeaderProps> = ({ pr }) => {
  const getStatusIcon = () => {
    switch (pr.state) {
      case 'open':
        return <GitPullRequest className="text-green-500" />;
      case 'merged':
        return <GitMerge className="text-purple-500" />;
      case 'closed':
        return <GitPullRequestClosed className="text-red-500" />;
      default:
        return <GitPullRequest />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">{getStatusIcon()}</div>
          <div className="flex-1 min-w-0">
            <a href={pr.repo_url + '/pull/' + pr.pr_number} target="_blank" rel="noopener noreferrer" className="hover:underline">
              <CardTitle className="text-2xl font-bold leading-7 text-gray-900 dark:text-gray-100 sm:truncate">
                {pr.title}
              </CardTitle>
            </a>
            <CardDescription className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-4">
              <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                <Badge variant={pr.state === 'open' ? 'default' : 'secondary'}>{pr.state}</Badge>
              </div>
              <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                <a
                  href={pr.repo_url + '/pull/' + pr.pr_number}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold hover:underline"
                >
                  #{pr.pr_number}
                </a>
                <span className="ml-1">opened by</span>
              </div>
              <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                <Avatar className="h-5 w-5 mr-1">
                  <AvatarImage src={pr.author_avatar_url} alt={pr.author_username} />
                  <AvatarFallback>{pr.author_username?.charAt(0)}</AvatarFallback>
                </Avatar>
                {pr.author_username}
              </div>
              <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                created at {new Date(pr.created_at).toLocaleString()}
              </div>
            </CardDescription>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};

export default PullRequestHeader;
